import React, { useEffect, useMemo, useState } from "react";
import api from "@api";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import "./TeamListAuth.css";

function TeamListAuth() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDupesOnly, setShowDupesOnly] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [error, setError] = useState("");
  const [selectedLeagueFilter, setSelectedLeagueFilter] = useState("");
  const [selectedSeasonFilter, setSelectedSeasonFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("az");
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchTeams = async () => {
      try {
        const response = await api.get("/api/teamsv2", {
          withCredentials: true,
        });

        const sortedTeams = (response.data.teams || []).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );

        setTeams(sortedTeams);
      } catch (err) {
        console.error("Error fetching teams:", err.response?.data || err.message);
        setError("Failed to fetch teams");
      }
    };

    fetchTeams();
  }, [isAuthenticated]);


  useEffect(() => {
    setSelectedLeagueFilter("");
  }, [selectedSeasonFilter]);
  const normalizeTeamName = (team) =>
    (team.name || "").toLowerCase().replace(/\s+/g, " ").trim();

  const teamsWithDuplicates = useMemo(() => {
    const nameCounts = {};

    teams.forEach((team) => {
      const key = normalizeTeamName(team);
      if (!key) return;
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    });

    return teams.map((team) => {
      const key = normalizeTeamName(team);
      return {
        ...team,
        isDuplicate: key && nameCounts[key] > 1,
      };
    });
  }, [teams]);

  const duplicateTeamCount = useMemo(() => {
    const nameCounts = {};

    teams.forEach((team) => {
      const key = normalizeTeamName(team);
      if (!key) return;
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    });

    return Object.values(nameCounts).filter((count) => count > 1).length;
  }, [teams]);



  const getTeamUsages = (team) => {
    return (
      team.seasons_leagues_teams ||
      team.seasonsLeaguesTeams ||
      team.team_usages ||
      []
    );
  };

  const getUsageInfo = (usage) => {
    const seasonLeague = usage.seasons_league || usage.seasonsLeague || {};
    const league = seasonLeague.league || usage.league || {};
    const season = seasonLeague.season || usage.season || {};

    return {
      id: usage.id,
      seasonId: season.id || usage.season_id || seasonLeague.season_id,
      leagueId: league.id || usage.league_id || seasonLeague.league_id,
      leagueName: league.name || "Unknown League",
      seasonName: season.name || "Unknown Season",
      captain: usage.captain || "",
      captainId: usage.captain_id || "",
      rosterCount:
        usage.roster_count ??
        usage.rosterCount ??
        usage.rosters?.length ??
        usage.players?.length ??
        0,
    };
  };

  const getUsageCount = (team) => getTeamUsages(team).length;

  const getCaptainNames = (team) => {
    const usages = getTeamUsages(team).map(getUsageInfo);

    const captains = usages
      .map((usage) => usage.captain)
      .filter(Boolean);

    return [...new Set(captains)];
  };

  const duplicateGroups = useMemo(() => {
    const groups = {};

    teamsWithDuplicates.forEach((team) => {
      if (!team.isDuplicate) return;

      const key = normalizeTeamName(team);
      if (!groups[key]) groups[key] = [];

      groups[key].push(team);
    });

    return Object.entries(groups).map(([name, groupTeams]) => {
      const sortedGroup = [...groupTeams].sort((a, b) => {
        const usageDiff = getUsageCount(b) - getUsageCount(a);
        if (usageDiff !== 0) return usageDiff;

        return a.id - b.id;
      });

      return {
        name,
        keepTeam: sortedGroup[0],
        joinTeams: sortedGroup.slice(1),
        teams: sortedGroup,
      };
    });
  }, [teamsWithDuplicates]);

  const handleJoinTeams = async (keepTeam, joinTeams) => {
    const joinIds = joinTeams.map((team) => team.id);

    if (!keepTeam?.id || joinIds.length === 0) return;

    const confirmJoin = window.confirm(
      `Join ${joinIds.length} team record${joinIds.length > 1 ? "s" : ""} into "${keepTeam.name}"?\n\nCaptains, rosters, games, leagues, and seasons will stay connected.`
    );

    if (!confirmJoin) return;

    try {
      await api.post(
        "/api/teamsv2/admin/join",
        {
          keepTeamId: keepTeam.id,
          joinTeamIds: joinIds,
        },
        { withCredentials: true }
      );

      const response = await api.get("/api/teamsv2", {
        withCredentials: true,
      });

      const sortedTeams = (response.data.teams || []).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );

      setTeams(sortedTeams);
      alert("Teams joined successfully.");
    } catch (err) {
      console.error("Error joining teams:", err.response?.data || err.message);
      alert("Failed to join teams.");
    }
  };

  const handleDeleteUnusedTeam = async (team) => {
    const usages = getTeamUsages(team);

    if (usages.length > 0) {
      alert("This team has season history. Join or archive this team instead.");
      return;
    }

    const confirmDelete = window.confirm(
      `Delete unused team "${team.name}"?\n\nThis team is not connected to any season, roster, or game.`
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/api/teamsv2/${team.id}`, {
        withCredentials: true,
      });

      setTeams((prev) => prev.filter((t) => t.id !== team.id));
      alert("Unused team deleted.");
    } catch (err) {
      console.error("Error deleting team:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Failed to delete team.");
    }
  };



  const filteredTeams = teamsWithDuplicates
    .filter((team) => {
      const teamName = (team.name || "").toLowerCase();
      const usages = getTeamUsages(team).map(getUsageInfo);

      const matchesSearch = teamName.includes(searchTerm.toLowerCase());

      const matchesDuplicateFilter = !showDupesOnly || team.isDuplicate;
      const matchesUnusedFilter =
        !showUnusedOnly || getTeamUsages(team).length === 0;
      const matchesLeague =
        !selectedLeagueFilter ||
        usages.some((usage) => usage.leagueId === Number(selectedLeagueFilter));

      const matchesSeason =
        !selectedSeasonFilter ||
        usages.some((usage) => usage.seasonId === Number(selectedSeasonFilter));

      return (
        matchesSearch &&
        matchesDuplicateFilter &&
        matchesUnusedFilter &&
        matchesLeague &&
        matchesSeason
      );
    })
    .sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();

      if (sortOrder === "za") {
        return nameB.localeCompare(nameA);
      }

      return nameA.localeCompare(nameB);
    });

  const leagues = useMemo(() => {
    const leagueMap = new Map();

    teams.forEach((team) => {
      getTeamUsages(team).forEach((usage) => {
        const info = getUsageInfo(usage);

        const matchesSelectedSeason =
          !selectedSeasonFilter ||
          info.seasonId === Number(selectedSeasonFilter);

        if (
          matchesSelectedSeason &&
          info.leagueId &&
          !leagueMap.has(info.leagueId)
        ) {
          leagueMap.set(info.leagueId, info.leagueName);
        }
      });
    });

    return Array.from(leagueMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teams, selectedSeasonFilter]);

  const seasons = useMemo(() => {
    const seasonMap = new Map();

    teams.forEach((team) => {
      getTeamUsages(team).forEach((usage) => {
        const info = getUsageInfo(usage);
        if (info.seasonId && !seasonMap.has(info.seasonId)) {
          seasonMap.set(info.seasonId, info.seasonName);
        }
      });
    });

    return Array.from(seasonMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teams]);



  return (
    <div className="teamList_auth">
      <div className="teamList-header">
        <div>
          <p className="teamList-eyebrow">Admin</p>
          <h2>Teams</h2>
          <p className="teamList-subtitle">
            Manage team identity records and review where each team is used.
          </p>
        </div>

        <div className="teamList-summary">
          <div>
            <span>{filteredTeams.length}</span>
            <p>Showing Teams</p>
          </div>
          <div>
            <span>{duplicateTeamCount}</span>
            <p>All Duplicate Names</p>
          </div>
        </div>
      </div>

      {error && <p className="teamList-error">{error}</p>}

      <div className="teamList-toolbar">
        <input
          type="text"
          placeholder="Search team name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />


        <select
          value={selectedSeasonFilter}
          onChange={(e) => setSelectedSeasonFilter(e.target.value)}
          className="teamList-filter-select"
        >
          <option value="">All Seasons</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {season.name}
            </option>
          ))}
        </select>
        <select
          value={selectedLeagueFilter}
          onChange={(e) => setSelectedLeagueFilter(e.target.value)}
          className="teamList-filter-select"
        >
          <option value="">All Leagues</option>
          {leagues.map((league) => (
            <option key={league.id} value={league.id}>
              {league.name}
            </option>
          ))}
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="teamList-filter-select"
        >
          <option value="az">A-Z</option>
          <option value="za">Z-A</option>
        </select>
        <label className="teamList-dupe-toggle">
          <input
            type="checkbox"
            checked={showDupesOnly}
            onChange={(e) => setShowDupesOnly(e.target.checked)}
          />
          <span>Show duplicates </span>
        </label>
        <label className="teamList-dupe-toggle">
          <input
            type="checkbox"
            checked={showUnusedOnly}
            onChange={(e) => setShowUnusedOnly(e.target.checked)}
          />
          <span>Show unused teams </span>
        </label>
      </div>

      <div className="teamList-dupe-status">
        {duplicateTeamCount > 0
          ? `⚠️ Found ${duplicateTeamCount} duplicate team name${duplicateTeamCount > 1 ? "s" : ""
          }`
          : "✅ No duplicate team names detected"}
      </div>

      {showDupesOnly && duplicateGroups.length > 0 && (
        <div className="teamList-join-groups">
          {duplicateGroups.map((group) => {
            const keepUsages = getTeamUsages(group.keepTeam).map(getUsageInfo);
            const keepCaptains = getCaptainNames(group.keepTeam);
            const allCaptains = group.teams.flatMap(getCaptainNames);
            const uniqueCaptains = [...new Set(allCaptains)];
            const hasDifferentCaptains = uniqueCaptains.length > 1;

            return (
              <div key={group.name} className="teamList-join-card">
                <div className="teamList-join-card-header">
                  <div>
                    <h3>{group.keepTeam.name}</h3>
                    <p>
                      {group.teams.length} matching team records found
                      {hasDifferentCaptains && (
                        <span className="teamList-captain-warning">
                          {" "}
                          · Different captains found
                        </span>
                      )}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="teamList-join-btn"
                    disabled={group.joinTeams.length === 0}
                    onClick={() => handleJoinTeams(group.keepTeam, group.joinTeams)}
                  >
                    Join Teams
                  </button>
                </div>

                <div className="teamList-join-layout">
                  <div className="teamList-keep-box">
                    <p className="teamList-box-label">Keep this team</p>
                    <div className="teamList-join-team-row">
                      <span>{group.keepTeam.name}</span>
                      <small>{getUsageCount(group.keepTeam)} time(s) used</small>
                    </div>

                    <div className="teamList-join-detail">
                      Captain:{" "}
                      {keepCaptains.length > 0
                        ? keepCaptains.join(", ")
                        : "No captain"}
                    </div>

                    {keepUsages.slice(0, 3).map((usage) => (
                      <div key={usage.id} className="teamList-join-detail">
                        {usage.leagueName} — {usage.seasonName}
                      </div>
                    ))}
                  </div>

                  <div className="teamList-join-box">
                    <p className="teamList-box-label">Join these into it</p>

                    {group.joinTeams.map((team) => {
                      const captains = getCaptainNames(team);
                      const usages = getTeamUsages(team).map(getUsageInfo);

                      return (
                        <div key={team.id} className="teamList-join-team-block">
                          <div className="teamList-join-team-row">
                            <span>{team.name}</span>
                            <small>{getUsageCount(team)} time(s) used</small>
                          </div>

                          <div className="teamList-join-detail">
                            Captain:{" "}
                            {captains.length > 0 ? captains.join(", ") : "No captain"}
                          </div>

                          {usages.slice(0, 3).map((usage) => (
                            <div key={usage.id} className="teamList-join-detail">
                              {usage.leagueName} — {usage.seasonName}
                            </div>
                          ))}

                          {usages.length === 0 && (
                            <div className="teamList-join-detail">
                              Not used yet. This record will be removed after joining.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {hasDifferentCaptains && (
                  <div className="teamList-join-note">
                    Captains are season-specific, so each season keeps its own captain
                    after joining.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!showDupesOnly && (
        <div className="teamList-table-wrap">
          <table className="teamList-table">
            <thead>
              <tr>
                <th>Team</th>
                <th>Team Entries</th>
                <th>Latest Captain</th>
                <th>Leagues / Seasons</th>
                <th>Manage</th>
              </tr>
            </thead>

            <tbody>
              {filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan="6" className="teamList-empty">
                    No teams found
                  </td>
                </tr>
              ) : (
                filteredTeams.map((team) => {
                  const usages = getTeamUsages(team).map(getUsageInfo);
                  const isOpen = !!expandedRows[team.id];
                  const shownUsages = isOpen ? usages : usages.slice(0, 2);
                  const latestUsage = usages[0];

                  return (
                    <tr key={team.id}>

                      <td>
                        <div>
                          <NavLink
                            to={`/dashboard/teamListAuth/${team.id}`}
                            className={
                              team.isDuplicate
                                ? "teamList-name teamList-name-dupe"
                                : "teamList-name"
                            }
                          >
                            {team.name || "Unnamed Team"}
                            {team.isDuplicate && " ⚠️"}
                          </NavLink>

                        </div>

                      </td>

                      <td>{usages.length}</td>

                      <td>
                        {latestUsage?.captain ? (
                          <span>
                            {latestUsage.captain}
                          </span>
                        ) : (
                          <span className="teamList-muted">No captain</span>
                        )}
                      </td>

                      <td>
                        {usages.length > 0 ? (
                          <div className="teamList-usage-list">
                            {shownUsages.map((usage, index) => (
                              <div key={usage.id || index} className="teamList-usage">
                                <span>
                                  {usage.leagueName} — {usage.seasonName}
                                </span>
                                <small>
                                  {usage.rosterCount} player
                                  {usage.rosterCount === 1 ? "" : "s"}
                                </small>
                              </div>
                            ))}

                            {usages.length > 2 && (
                              <button
                                type="button"
                                className="teamList-show-more-btn"
                                onClick={() =>
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [team.id]: !isOpen,
                                  }))
                                }
                              >
                                {isOpen
                                  ? "Show less"
                                  : `+${usages.length - 2} more`}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="teamList-muted">Not used yet</span>
                        )}
                      </td>

                      <td>
                        <div className="teamList-actions">
                          <NavLink
                            className="teamList-manage-btn"
                            to={`/dashboard/teamListAuth/${team.id}`}
                          >
                            Manage
                          </NavLink>

                          {getTeamUsages(team).length === 0 && (
                            <button
                              type="button"
                              className="teamList-delete-unused-btn"
                              onClick={() => handleDeleteUnusedTeam(team)}
                            >
                              Delete Unused
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TeamListAuth;